import React, { useState, useCallback, useEffect } from 'react';
import {
  Paper, Typography, Box, IconButton, Accordion, AccordionSummary, AccordionDetails,
  Chip, List, ListItem, ListItemText, CircularProgress, Alert, Button, Grid,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { formatCurrency } from '../../utils/format';

const PAGE_SIZE = 5;

const STATUS_META = {
  matched: { label: 'ตรงกัน', color: 'success' },
  mismatched: { label: 'ไม่ตรงกัน', color: 'warning' },
  not_found: { label: 'ไม่พบในรูป', color: 'default' },
};

function ResultList({ items }) {
  if (items.length === 0) return null;
  return (
    <List dense disablePadding>
      {items.map((item) => (
        // eslint-disable-next-line no-underscore-dangle
        <ListItem key={item.payment || item.payment_number} divider disableGutters sx={{ px: 1 }}>
          <ListItemText
            primary={
              <>
                {item.payment_number} — {formatCurrency(item.totalAmount)} บาท
                {' '}
                <Chip
                  size="small"
                  color={STATUS_META[item.status]?.color || 'default'}
                  label={STATUS_META[item.status]?.label || item.status}
                  sx={{ ml: 0.5 }}
                />
              </>
            }
            secondary={item.date ? new Date(item.date).toLocaleDateString('th-TH') : ''}
          />
        </ListItem>
      ))}
    </List>
  );
}

// รายการในรูปที่ OCR อ่านได้ แต่ไม่ถูกใช้จับคู่กับ payment ที่รอยืนยันตัวไหนเลยตอน confirm
// (ดู matchOcrTransactions/confirmOcrVerification ฝั่ง accounting.service.js) — เช่นรายการ
// เบิกที่ยังไม่ถูกบันทึกเข้าระบบ หรือธุรกรรมอื่นที่ไม่เกี่ยวกับรายจ่าย/รายรับที่ระบบติดตาม
function UnmatchedTransactionsList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <List dense disablePadding>
      {items.map((t, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <ListItem key={idx} divider disableGutters sx={{ px: 1 }}>
          <ListItemText
            primary={t.withdraw != null ? `ถอน ${formatCurrency(t.withdraw)} บาท` : `ฝาก ${formatCurrency(t.deposit)} บาท`}
            secondary={`${t.date}${t.time ? ` ${t.time}` : ''}${t.description ? ` — ${t.description}` : ''}`}
          />
        </ListItem>
      ))}
    </List>
  );
}

function HistoryEntry({ entry }) {
  const account = entry.account || {};
  const matched = (entry.results || []).filter((r) => r.status === 'matched');
  const mismatched = (entry.results || []).filter((r) => r.status === 'mismatched');
  const notFound = (entry.results || []).filter((r) => r.status === 'not_found');
  const unmatchedTransactions = entry.unmatched_transactions || [];

  return (
    <Accordion variant="outlined" sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
          <Typography variant="body2">
            {new Date(entry.createdAt).toLocaleString('th-TH')} — ยืนยันแล้ว {(entry.confirmedPayments || []).length} รายการ
          </Typography>
          <Chip size="small" color="success" label={`ตรงกัน ${matched.length}`} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {(account.account_name || account.account_number) && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            บัญชี: {account.account_name || '-'} {account.account_number ? `(${account.account_number})` : ''}
          </Typography>
        )}

        {(entry.images || []).length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {entry.images.map((img) => (
              <a key={img.id || img._id} href={img.url} target="_blank" rel="noreferrer">
                <Box
                  component="img"
                  src={img.url}
                  alt="สลิป/หน้าจอธนาคาร"
                  sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1, border: '1px solid #e0e0e0' }}
                />
              </a>
            ))}
          </Box>
        )}

        <Typography variant="subtitle2" gutterBottom>ผลเปรียบเทียบกับ Payment ในระบบ</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Chip size="small" color="success" label={`ตรงกัน ${matched.length}`} />
          <Chip size="small" color="warning" label={`ไม่ตรงกัน ${mismatched.length}`} />
          <Chip size="small" label={`ไม่พบในรูป ${notFound.length}`} />
        </Box>
        <ResultList items={entry.results || []} />

        {unmatchedTransactions.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
              รายการในรูปที่ไม่มีรายการเบิกในระบบ ({unmatchedTransactions.length})
            </Typography>
            <UnmatchedTransactionsList items={unmatchedTransactions} />
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export function OcrVerificationHistoryCard({ customerId }) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (pageToLoad, append) => {
    if (!customerId) return;
    setLoading(true);
    setError('');
    try {
      const data = await dispatch(actions.listOcrVerificationHistory({
        customerId, page: pageToLoad, size: PAGE_SIZE,
      }));
      setRows((prev) => (append ? [...prev, ...data.rows] : data.rows));
      setTotal(data.total);
      setPage(pageToLoad);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, dispatch]);

  useEffect(() => {
    load(1, false);
  }, [load]);

  const hasMore = rows.length < total;

  if (!customerId) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1">ประวัติการยืนยัน OCR ({total})</Typography>
        <IconButton size="small" onClick={() => load(1, false)} disabled={loading}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {rows.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary">ยังไม่มีประวัติการยืนยัน OCR</Typography>
      )}

      {rows.map((entry) => (
        // eslint-disable-next-line no-underscore-dangle
        <HistoryEntry key={entry.id || entry._id} entry={entry} />
      ))}

      {loading && (
        <Grid container justifyContent="center" sx={{ py: 2 }}>
          <CircularProgress size={24} />
        </Grid>
      )}

      {hasMore && !loading && (
        <Button size="small" onClick={() => load(page + 1, true)} sx={{ mt: 1 }}>
          โหลดเพิ่มเติม
        </Button>
      )}
    </Paper>
  );
}

export default OcrVerificationHistoryCard;
